import React from 'react';
import { PromptTemplate, ReviewCapabilityFields } from '../../../api/structs';
import { AIModel } from '../../../api/structs/model';
import ReviewCapabilitiesFields from './ReviewCapabilitiesFields';
import ReviewModelsAndGates, {
  ResolvedReviewTiers,
  ReviewTierFields,
} from './ReviewModelsAndGates';
import { InheritedStatus, fieldCaption } from './reviewFormStyles';
import { ResolvedReviewCapabilities } from './resolveReviewCapabilities';

export type ReviewLayer = 'account' | 'pipeline' | 'checkpoint';

/** What the layer above is called, in the words the captions use. */
const INHERITS_FROM: Record<ReviewLayer, string> = {
  account: 'the built-in default',
  pipeline: 'your account',
  checkpoint: 'the pipeline',
};

const LAYER_CAPTION: Record<ReviewLayer, string> = {
  account:
    'Your baseline for every pipeline. A pipeline’s reviewer defaults, and then a checkpoint’s own review block, can narrow or widen any of it.',
  pipeline:
    'This pipeline’s defaults, over your account settings. A checkpoint’s own review block still wins over these.',
  checkpoint:
    'This checkpoint only, over the pipeline’s reviewer defaults. Unset fields inherit — they are not off.',
};

export interface ReviewCapabilitiesFormProps {
  /** The ten capability fields this layer declares. Absent key = inherit. */
  value: ReviewCapabilityFields;
  /** What the layers ABOVE resolve to, for the "inherits: …" captions. */
  inherited: ResolvedReviewCapabilities;
  layer: ReviewLayer;
  /**
   * The complete next value, with unset fields DELETED rather than set to
   * `undefined` — the difference is what "inherit" means on the wire.
   */
  onChange: (next: ReviewCapabilityFields) => void;
  /** Models/gates/mode/judge template, for the layers that carry them. */
  tiers?: ReviewTierFields;
  onTiersChange?: (next: ReviewTierFields) => void;
  /** What the layers above resolve each judge tier to, for its caption. */
  inheritedTiers: ResolvedReviewTiers;
  promptTemplates?: PromptTemplate[];
  models?: AIModel[];
  /** The pair judge only exists where a closing frame does. */
  showPairJudge: boolean;
  /**
   * Whether `inherited` / `inheritedTiers` have actually been read yet. The
   * account layer has nothing above it, so it leaves this at `ready`.
   */
  inheritedStatus?: InheritedStatus;
}

/**
 * The ONE reviewer capability form, used by all three layers of the cascade:
 * Settings → Reviewer (the account baseline), a pipeline's `review_defaults`,
 * and a checkpoint's own `review` block.
 *
 * One form because they edit the same ten fields with the same meanings — a
 * second copy would drift, and the layer that drifted would be the one whose
 * captions lied about what the other two do. What differs between layers is
 * only what the captions point at and which extra knobs the layer carries;
 * both are props.
 */
const ReviewCapabilitiesForm: React.FC<ReviewCapabilitiesFormProps> = ({
  value,
  inherited,
  layer,
  onChange,
  tiers,
  onTiersChange,
  inheritedTiers,
  promptTemplates = [],
  models = [],
  showPairJudge,
  inheritedStatus,
}) => {
  // The canvas inspector renders TWICE (desktop rail + mobile sheet), so every
  // control id below has to be unique per mounted form or one `<label for>`
  // would drive the other copy's control.
  const idPrefix = React.useId();
  // `pair` is a capability field that happens to be a model reference, so it is
  // shown beside the other tiers but written back into the capability block.
  const tierValue: ReviewTierFields = { ...(tiers || {}), ...(value.pair ? { pair: value.pair } : {}) };

  // Exactly one control moves per event, so the two halves are written one at
  // a time. Writing BOTH would be a bug rather than thoroughness: each half is
  // rebuilt from the same `value`/`tiers` snapshot, so the second write would
  // land on a block that does not yet carry the first and quietly undo it.
  const handleTiersChange = (next: ReviewTierFields) => {
    const { pair, ...rest } = next;
    if (pair !== value.pair) {
      if (pair === undefined) {
        const withoutPair = { ...value };
        delete withoutPair.pair;
        onChange(withoutPair);
      } else {
        onChange({ ...value, pair });
      }
      return;
    }
    if (onTiersChange) onTiersChange(rest);
  };

  return (
    <div className="space-y-4" data-testid="review-capabilities-form">
      <p className={fieldCaption}>{LAYER_CAPTION[layer]}</p>

      <ReviewCapabilitiesFields
        value={value}
        inherited={inherited}
        inheritsFrom={INHERITS_FROM[layer]}
        showPairJudge={showPairJudge}
        inheritedStatus={inheritedStatus}
        idPrefix={idPrefix}
        onChange={onChange}
      />

      {(tiers !== undefined || showPairJudge) && (
        <div className="border-t border-[var(--border-subtle)] pt-3">
          <ReviewModelsAndGates
            value={tierValue}
            inheritedTiers={inheritedTiers}
            inheritsFrom={INHERITS_FROM[layer]}
            models={models}
            showMode={layer === 'pipeline'}
            showJudgeTemplate={layer === 'checkpoint'}
            showPair={showPairJudge}
            showCheckConfirm={tiers !== undefined}
            inheritedStatus={inheritedStatus}
            idPrefix={idPrefix}
            promptTemplates={promptTemplates}
            onChange={handleTiersChange}
          />
        </div>
      )}
    </div>
  );
};

export default ReviewCapabilitiesForm;
